import { ComponentPortal } from '@angular/cdk/portal';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { Injectable, Injector, Type } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { bringFloatingDialogToFront, FloatingDialogComponent } from '../../component/floating-dialog/floating-dialog.component';
import { FloatingDialogRef } from './floating-dialog-ref';
import { FLOATING_DIALOG_DATA, FLOATING_DIALOG_DEFAULT_CONFIG, FloatingDialogConfig, ResolvedFloatingDialogConfig } from './floating-dialog-config';

@Injectable()
export class FloatingDialogService {

  private readonly openDialogsById = new Map<string, { dialogRef: FloatingDialogRef; overlayRef: OverlayRef }>();

  private readonly openDialogs: { dialogRef: FloatingDialogRef; overlayRef: OverlayRef }[] = [];

  private readonly escapeListener = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape' || event.defaultPrevented) return;
    const hosts = Array.from(this.openDialogs[0]?.overlayRef.hostElement.parentElement?.children ?? []);
    const top = this.openDialogs.reduce((candidate, entry) =>
      hosts.indexOf(entry.overlayRef.hostElement) > hosts.indexOf(candidate.overlayRef.hostElement)
        ? entry : candidate, this.openDialogs[0]);
    if (top?.dialogRef.closeOnEscape)
      top.dialogRef.close();
  };

  constructor(private overlay: Overlay, private injector: Injector, private router: Router) {
  }

  getDialogById(id: string): FloatingDialogRef | undefined {
    return this.openDialogsById.get(id)?.dialogRef;
  }

  closeAll(): void {
    [...this.openDialogs].forEach(entry => entry.dialogRef.forceClose());
  }

  open<T, D = any, R = any>(component: Type<T>, config: FloatingDialogConfig<D> = {}): FloatingDialogRef<T, R> {
    const resolvedConfig = this.withDefaults(config);

    if (resolvedConfig.id != null) {
      const existing = this.openDialogsById.get(resolvedConfig.id);
      if (existing) {
        bringFloatingDialogToFront(existing.overlayRef.hostElement);
        return existing.dialogRef as FloatingDialogRef<T, R>;
      }
    }

    const dialogRef = new FloatingDialogRef<T, R>();
    dialogRef.closeOnEscape = resolvedConfig.closeOnEscape;

    const overlayRef = this.overlay.create({
      hasBackdrop: resolvedConfig.modal || resolvedConfig.closeOnOutsideClick,
      backdropClass: resolvedConfig.modal ? 'cdk-overlay-dark-backdrop' : 'cdk-overlay-transparent-backdrop',
      positionStrategy: this.overlay.position().global().centerHorizontally().centerVertically(),
      scrollStrategy: this.overlay.scrollStrategies.noop()
    });

    const containerInjector = Injector.create({
      parent: resolvedConfig.injector ?? this.injector,
      providers: [
        { provide: FloatingDialogRef, useValue: dialogRef },
        { provide: OverlayRef, useValue: overlayRef }
      ]
    });

    const containerRef = overlayRef.attach(new ComponentPortal(FloatingDialogComponent, null, containerInjector));
    containerRef.instance.applyConfig(resolvedConfig);

    const contentInjector = Injector.create({
      parent: resolvedConfig.injector ?? this.injector,
      providers: [
        { provide: FloatingDialogRef, useValue: dialogRef },
        { provide: FLOATING_DIALOG_DATA, useValue: resolvedConfig.data }
      ]
    });

    const contentRef = containerRef.instance.attachContent(new ComponentPortal(component, null, contentInjector));
    dialogRef.componentInstance = contentRef.instance;

    if (resolvedConfig.id != null)
      this.openDialogsById.set(resolvedConfig.id, { dialogRef, overlayRef });
    this.openDialogs.push({ dialogRef, overlayRef });
    if (this.openDialogs.length === 1)
      document.addEventListener('keydown', this.escapeListener);

    let navigationSubscription: Subscription | null = null;
    let closed = false;

    dialogRef._registerClose((result?: R) => {
      if (closed) return;
      closed = true;

      overlayRef.overlayElement.style.display = 'none';
      overlayRef.backdropElement?.style.setProperty('display', 'none');

      if (resolvedConfig.id != null && this.openDialogsById.get(resolvedConfig.id)?.dialogRef === dialogRef)
        this.openDialogsById.delete(resolvedConfig.id);
      const openIndex = this.openDialogs.findIndex(entry => entry.dialogRef === dialogRef);
      if (openIndex !== -1)
        this.openDialogs.splice(openIndex, 1);
      if (this.openDialogs.length === 0)
        document.removeEventListener('keydown', this.escapeListener);
      navigationSubscription?.unsubscribe();
      dialogRef._emitClosed(result);

      setTimeout(() => overlayRef.dispose());
    });

    if (resolvedConfig.closeOnNavigation) {
      navigationSubscription = this.router.events
        .pipe(filter(event => event instanceof NavigationStart), take(1))
        .subscribe(() => dialogRef.forceClose());
    }

    if (resolvedConfig.closeOnOutsideClick) {
      overlayRef.backdropClick().subscribe(() => dialogRef.close());
    }
    return dialogRef;
  }

  private withDefaults<D>(config: FloatingDialogConfig<D>): ResolvedFloatingDialogConfig<D> {
    return {
      ...FLOATING_DIALOG_DEFAULT_CONFIG,
      ...config
    };
  }
}
